/**
 * Department master codes that show / persist user coverage fields
 * (destinations, hauls, trip types, international/domestic scope).
 * Seeded in the departments master - match by `code`, not display name.
 */
export const USER_COVERAGE_DEPARTMENT_CODES = [
  'admin',
  'sales',
  'domestic_sales',
  'international_sales',
] as const;

export type UserCoverageDepartmentCode = (typeof USER_COVERAGE_DEPARTMENT_CODES)[number];

/**
 * Operations department master codes (2026-10-30). Only a user in one of
 * these departments sits on an ops desk (Flight / DMC-Hotel / Visa), so the
 * desk picker lives under the Department field and is cleared elsewhere.
 * Seeded code is `operations`; `ops` is accepted for a hand-created master.
 */
export const OPERATIONS_DEPARTMENT_CODES = ['operations', 'ops'] as const;

export type OperationsDepartmentCode = (typeof OPERATIONS_DEPARTMENT_CODES)[number];

/** Sales-family departments - primary destination (P1) is required on create. */
export const SALES_DEPARTMENT_CODES = ['sales', 'domestic_sales', 'international_sales'] as const;

export type SalesDepartmentCode = (typeof SALES_DEPARTMENT_CODES)[number];

function normalizeDepartmentCode(code: string): string {
  return code.trim().toLowerCase();
}

export function isUserCoverageDepartmentCode(code: string | null | undefined): boolean {
  if (!code) return false;
  return (USER_COVERAGE_DEPARTMENT_CODES as readonly string[]).includes(
    normalizeDepartmentCode(code),
  );
}

export function isOperationsDepartmentCode(code: string | null | undefined): boolean {
  if (!code) return false;
  return (OPERATIONS_DEPARTMENT_CODES as readonly string[]).includes(normalizeDepartmentCode(code));
}

export function isSalesDepartmentCode(code: string | null | undefined): boolean {
  if (!code) return false;
  return (SALES_DEPARTMENT_CODES as readonly string[]).includes(normalizeDepartmentCode(code));
}

/**
 * Roles that sell. A user in one of these roles must carry a complete
 * sales coverage (scope, haul, trip types and at least one destination):
 * round-robin routes leads by exactly these four, so a gap here means a
 * seller who silently never gets a lead.
 */
export const SALES_ROLES = ['SALES_LEAD', 'SALES_AGENT'] as const;

export type SalesRole = (typeof SALES_ROLES)[number];

export function isSalesRole(role: string | null | undefined): role is SalesRole {
  if (!role) return false;
  return (SALES_ROLES as readonly string[]).includes(role);
}

/**
 * Roles that book (2026-10-30). An operations user carries the same coverage
 * picks as a seller (scope, haul, destinations) but with no capacity and no
 * required fields: the desk rotation prefers anyone whose coverage matches
 * the lead on ANY one dimension, and a user with no picks takes everything.
 */
export const OPERATIONS_ROLES = ['OPS', 'OPERATIONS_LEAD', 'OPERATIONS_EXECUTIVE'] as const;

export type OperationsRole = (typeof OPERATIONS_ROLES)[number];

export function isOperationsRole(role: string | null | undefined): role is OperationsRole {
  if (!role) return false;
  return (OPERATIONS_ROLES as readonly string[]).includes(role);
}

/** A role whose user form shows the coverage panel (sellers required, ops optional). */
export function isCoverageRole(role: string | null | undefined): boolean {
  return isSalesRole(role) || isOperationsRole(role);
}

/** The four coverage dimensions, each as "how many were picked". */
export interface SalesCoverageCounts {
  salesScopes?: ReadonlyArray<unknown> | null;
  salesHauls?: ReadonlyArray<unknown> | null;
  salesTripTypeIds?: ReadonlyArray<unknown> | null;
  destinations?: ReadonlyArray<unknown> | null;
}

export type SalesCoverageField = keyof SalesCoverageCounts;

export const SALES_COVERAGE_REQUIRED_MESSAGE: Record<SalesCoverageField, string> = {
  salesScopes: 'Pick International, Domestic or both for a sales role',
  salesHauls: 'Pick Short Haul, Long Haul or both for a sales role',
  salesTripTypeIds: 'Pick at least one trip type for a sales role',
  destinations: 'Add at least one destination for a sales role',
};

/**
 * Haul (short / long) only means something for international trips, so a
 * seller covering Domestic alone is not asked for one. With no scope picked
 * yet the haul still counts as missing - the scope error leads anyway.
 */
export function salesHaulRequired(salesScopes: ReadonlyArray<unknown> | null | undefined): boolean {
  if (!salesScopes || salesScopes.length === 0) return true;
  return salesScopes.includes('INTERNATIONAL');
}

/** Which of the four are empty. `[]` = the coverage is complete. */
export function missingSalesCoverage(coverage: SalesCoverageCounts): SalesCoverageField[] {
  return (Object.keys(SALES_COVERAGE_REQUIRED_MESSAGE) as SalesCoverageField[]).filter((key) => {
    if (key === 'salesHauls' && !salesHaulRequired(coverage.salesScopes)) return false;
    return (coverage[key]?.length ?? 0) === 0;
  });
}

/** @deprecated Prefer `isUserCoverageDepartmentCode` - kept for existing imports. */
export function requiresUserSalesCoverage(departmentCode: string | null | undefined): boolean {
  return isUserCoverageDepartmentCode(departmentCode);
}
